<template>
  <div class="diet-list">
    <SystemDietListToolbar
      :copy-menu-visible="copySelectedDietMenu.visible"
      :diet-type="dietType"
      :drag-to-nav="dragToNav"
      :groups="groups"
      :some-diets-selected="someDietsSelected"
      @close-copy="hideCopySelectedDietMenu"
      @copy="copySelectedDiet"
      @drop="handleDietDrop"
      @show-copy="showCopySelectedDietMenu"
    />
    <SystemDietTable
      :all-selected="allDietsSelected"
      :calories-sort-icon="getIconBySort('calories')"
      :copy-menu-visible="copySingleDietMenu.visible"
      :diets="sortedDiets"
      :diet-type="dietType"
      :groups="groups"
      :is-hovered="isHoveredDiet"
      :loading="loading"
      :name-sort-icon="getIconBySort('name')"
      @close-copy="hideCopySingleDietMenu"
      @copy="copySingleDiet"
      @copy-patient="showCopyToPatientModal"
      @dragend="handleDietDragEnd"
      @dragstart="handleDietDragStart"
      @hover="setHoverDiet"
      @select="selectDiet"
      @select-all="handleAll"
      @show-copy="showCopySingleDietMenu"
      @sort="setSortBy"
    />
    <DietCopyToPatientModal
      v-if="copyToPatientModal.visible"
      :diet-type="dietType"
      :diet-id="copyToPatientModal.dietId"
      @close="hideCopyToPatientModal"
      @send-system-diets-list-to-file="sendSystemDiets"
    />
    <div id="ghost-image" class="ghost-image">
      <BaseIcon class="ghost-image__icon" name="assignment" />
      {{ ghostText() }}
    </div>
  </div>
</template>

<script lang="ts" setup>
  import BaseIcon from '@/components/BaseIcon.vue'
  import DietCopyToPatientModal from '@/components/diets/DietCopyToPatientModal.vue'

  import SystemDietListToolbar from './components/SystemDietListToolbar/SystemDietListToolbar.vue'
  import SystemDietTable from './components/SystemDietTable/SystemDietTable.vue'
  import { useSystemDietList } from './composables/useSystemDietList'

  const {
    allDietsSelected,
    copySelectedDiet,
    copySelectedDietMenu,
    copySingleDiet,
    copySingleDietMenu,
    copyToPatientModal,
    dietType,
    dragToNav,
    getIconBySort,
    ghostText,
    groups,
    handleAll,
    handleDietDragEnd,
    handleDietDragStart,
    handleDietDrop,
    hideCopySelectedDietMenu,
    hideCopySingleDietMenu,
    hideCopyToPatientModal,
    isHoveredDiet,
    loading,
    selectDiet,
    sendSystemDiets,
    setHoverDiet,
    setSortBy,
    showCopySelectedDietMenu,
    showCopySingleDietMenu,
    showCopyToPatientModal,
    someDietsSelected,
    sortedDiets,
  } = useSystemDietList()
</script>

<style lang="scss">
  .diet-list {
    min-width: 1050px;
    margin-bottom: 24px;

  .nav-actions {
    display: flex;
  }

  .actions {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .list {
    position: relative;
    padding: 0 12px 12px;
    font-size: 12px;
    background: #fff;
  }

  .list-loader {
    position: absolute;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: top;
    justify-content: center;
    cursor: wait;
    background: rgb(255 255 255 / 40%);
  }

  .list-loader-bar {
    position: relative;
    width: calc(100% - 24px);
    height: 2px;
    margin: 52px 12px;
    overflow: hidden;
    background-color: transparent;
    border-radius: 9999px;
  }

  .list-loader-bar__progress {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 50%;
    background-color: var(--color-main-100);
    border-radius: 9999px;
    animation-name: progress-bar;
    animation-duration: 3s;
    animation-iteration-count: infinite;
  }

  @keyframes progress-bar {
    from {
      left: -50%;
    }

    to {
      left: 100%;
    }
  }

  .row {
    display: grid;
    grid-template-columns: 24px minmax(165px, auto) 180px;
    column-gap: 10px;
  }

  .row__actions {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    display: none;
  }

  .column {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .column__sortable {
    display: flex;
    align-items: center;
    height: 52px;
    cursor: pointer;
  }

  .column__sortable .base-icon {
    cursor: pointer;
  }

  .header {
    height: 52px;
    margin-bottom: 12px;
    font-family: Montserrat-Medium;
    color: var(--color-main-100);
    border-bottom: 2px solid #eaeaea;
  }

  .header__name {
    justify-content: left;
  }

  .diets-empty {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 20px 0;
  }

  .diets {
    position: relative;
  }

  .diets-loader {
    position: absolute;
    inset: 0;
    z-index: 10000;
    cursor: wait;
    background: rgb(255 255 255 / 60%);
  }

  .diet {
    position: relative;
    height: 32px;
    margin-bottom: 6px;
  }

  .diet:hover,
  .diet--selected {
    background: var(--color-main-10);
  }

  .diet__name {
    justify-content: left;
  }

  .diet__name > a {
    display: block;
    width: 100%;
    height: 32px;
    line-height: 32px;
    color: var(--color-primary-text);
    text-decoration: none;
  }

  .diet__calories {
    justify-content: flex-end;
    margin-right: 75px;
  }

  .diet__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 32px;
    padding: 0 10px 0 50px;
    background: linear-gradient(to right, transparent, var(--color-main-10) 50px);
  }

  .diet:hover .row__actions {
    display: block;
  }

  .copy-diet-wrapper {
    position: relative;
  }

  .copy-diet-menu {
    top: 12px;
    right: 12px;
  }

  .ghost-image {
    position: absolute;
    top: -500px;
    display: flex;
    align-items: center;
    padding: 6px;
    font-family: Montserrat-SemiBold;
    font-size: 12px;
    color: #fff;
    background-color: #5dbab3;
    border-radius: 5px;
  }

  .ghost-image__icon {
    padding-right: 6px;
    color: #fff;
  }
  }
</style>
